import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { Helmet } from "react-helmet-async";
import { GUIDES } from "@/data/guides";

const GUIDES_BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.videoileo.com/" },
    { "@type": "ListItem", position: 2, name: "Guides", item: "https://www.videoileo.com/guides" },
  ],
};
import { CreateathonBanner } from "@/components/CreateathonBanner";

export default function Guides() {
  const [dynGuides, setDynGuides] = useState([]);
  useEffect(() => {
    api.get("/guides").then((r) => setDynGuides(r.data.guides || [])).catch(() => {});
  }, []);
  const allGuides = [...dynGuides, ...GUIDES];
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="guides-page">
      <CreateathonBanner />
      <Seo
        title="Faceless Video Guides"
        description="Free guides on starting a faceless YouTube channel, AI voiceovers, YouTube automation, profitable niches and growing subscribers with video series."
        path="/guides"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(GUIDES_BREADCRUMB)}</script>
      </Helmet>
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandWordmark className="h-12" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/register" data-testid="guides-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-colors">
              Start free
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-14">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Guides</p>
        <h1 className="font-head text-4xl sm:text-5xl tracking-tighter font-black mb-4">The faceless video playbook</h1>
        <p className="text-zinc-400 max-w-xl mb-12 leading-relaxed">
          Free, practical guides on starting and growing a faceless channel — niches, AI voiceovers, automation and series that win subscribers.
        </p>

        <div className="space-y-4">
          {allGuides.map((g) => (
            <Link
              key={g.slug}
              to={`/guides/${g.slug}`}
              data-testid={`guide-card-${g.slug}`}
              className="block border border-white/10 rounded-md bg-[#121212] overflow-hidden hover:border-blue-500/50 hover:bg-[#151515] transition-colors group"
            >
              <img src={g.image} alt={g.title} loading="lazy" className="w-full h-44 object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="p-6">
                <h2 className="font-head text-base md:text-lg font-semibold mb-1 group-hover:text-blue-300 transition-colors">{g.title}</h2>
                <p className="text-sm text-zinc-500 leading-relaxed line-clamp-2">{g.description}</p>
                <p className="text-xs text-zinc-600 mt-2 inline-flex items-center gap-1.5">
                  {g.readTime} <ArrowRight className="h-3 w-3" />
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <NewsletterSignup />
        </div>
      </main>

      <footer className="border-t border-white/10 py-10 px-5 text-center text-sm text-zinc-600">
        © 2026 Videoileo · The faceless video studio.
        <span className="mx-2">·</span>
        <Link to="/" className="text-zinc-500 hover:text-zinc-300 underline">Home</Link>
        <span className="mx-2">·</span>
        <Link to="/pricing" className="text-zinc-500 hover:text-zinc-300 underline">Pricing</Link>
      </footer>
    </div>
  );
}
